import {
  format,
  addSeconds,
  startOfToday,
} from 'date-fns';
import isObject from 'lodash/isObject';
import formatNumber from '@framework/data-helpers/formatters/formatNumber';
import { MAP_FILTERS_NAME, MAP_FILTERS_PLURAL_NAME, MAP_STATUS_LABEL } from './constants';
import { MONTH_DAY_YEAR_FORMAT, TIME_FORMAT_WITH_SECONDS } from '@dealroadshow/uikit-date-time/dateFormats';
import getDateFromUnixTime from '@framework/date-helpers/getDateFromUnixTime';
import setLocalZone from '@framework/date-helpers/setLocalZone';

interface IMaxMinValues {
  minValue: number,
  maxValue: number,
}

interface IPeriod {
  dateFrom: number,
  dateTo: number,
  timeZone: string,
}

interface IRange {
  range: {
    min: number,
    max: number,
  },
}

interface ICurrentFaceRangePresetItem {
  currentFaceRangeId: string,
  currentFaceRangeFromValue?: number,
  currentFaceRangeToValue?: number,
}

export interface IFiltersFormatterConfig {
  tradeSideLabelMap?: Record<string, string>,
  tradeStatusLabelMap?: Record<string, string>,
  currentFaceRangePreset?: ICurrentFaceRangePresetItem[],
}

const includedRegistrationGroupIdsFilter =
  (filterName: string, filterValue: string[]) => ((Array.isArray(filterValue) && filterValue.length === 1)
    ? `${ filterValue.length } ${ MAP_FILTERS_NAME[filterName] }`
    : `${ filterValue.length } ${ MAP_FILTERS_PLURAL_NAME[filterName] }`);

const includedStatusFilter = (
  filterName: string,
  filterValue: string,
) => `${ MAP_FILTERS_NAME[filterName] }: ${ MAP_STATUS_LABEL[filterValue] }`;

const includedTradeIdsFilter = (
  filterName: string,
  filterValue: string | string[],
  labelMap: Record<string, string>,
) => {
  const value = Array.isArray(filterValue)
    ? filterValue.map((value) => labelMap[value]).join(', ')
    : labelMap[filterValue];

  return `${ MAP_FILTERS_NAME[filterName] }: ${ value }`;
};

const spreadRangeFilter = (
  filterName: string,
  filterValue: IRange,
) => {
  if (
    isObject(filterValue) &&
    ('range' in filterValue) &&
    (('min' in filterValue.range) &&
    ('max' in filterValue.range))
  ) {
    const value = `${ formatNumber(filterValue.range.min) }-${ formatNumber(filterValue.range.max) }`;

    return `${ MAP_FILTERS_NAME[filterName] }: ${ value }`;
  }

  return undefined;
};

const getDateFromToRangeFilter = (
  filterName: string,
  { dateFrom, dateTo, timeZone }: IPeriod,
) => {
  if (dateFrom && dateTo) {
    const getDateFromUnix = (timestamp, timeZone) => (timeZone ?
      // @ts-ignore
      getDateFromUnixTime(setLocalZone(timeZone, timestamp)) : getDateFromUnixTime(timestamp));
    const dateFromFromUnix = getDateFromUnix(dateFrom, timeZone);
    const dateToFromUnix = getDateFromUnix(dateTo, timeZone);
    const value = `${ format(dateFromFromUnix, MONTH_DAY_YEAR_FORMAT) } - ${ format(dateToFromUnix, MONTH_DAY_YEAR_FORMAT) }`;

    return `${ MAP_FILTERS_NAME[filterName] }: ${ value }`;
  }

  return undefined;
};

const allocationSizeRangeFilter = (
  filterName: string,
  filterValue: IRange,
) => {
  if (
    isObject(filterValue) &&
    ('range' in filterValue) &&
    (('min' in filterValue.range) &&
    ('max' in filterValue.range))
  ) {
    const min = formatNumber(Math.round(filterValue.range.min / 1000000));
    const max = formatNumber(Math.round(filterValue.range.max / 1000000));

    return `${ MAP_FILTERS_NAME[filterName] }: ${ min }-${ max }`;
  }

  return undefined;
};

const speedRangeFilter = (
  filterName: string,
  filterValue: IRange,
) => {
  if (
    isObject(filterValue) &&
    ('range' in filterValue) &&
    (('min' in filterValue.range) &&
    ('max' in filterValue.range))
  ) {
    return `${ MAP_FILTERS_NAME[filterName] }: ${ filterValue.range.min }-${ filterValue.range.max }`;
  }

  return undefined;
};

const dateRangeFilter = (
  filterName: string,
  filterValue: IRange,
) => {
  if (
    isObject(filterValue) &&
    ('range' in filterValue) &&
    (('min' in filterValue.range) &&
    ('max' in filterValue.range))
  ) {
    const dateFromFromUnix = getDateFromUnixTime(filterValue.range.min);
    const dateToFromUnix = getDateFromUnixTime(filterValue.range.max);
    const value = `${ format(dateFromFromUnix, MONTH_DAY_YEAR_FORMAT) } - ${ format(dateToFromUnix, MONTH_DAY_YEAR_FORMAT) }`;

    return `${ MAP_FILTERS_NAME[filterName] }: ${ value }`;
  }

  return undefined;
};

const yieldRangeFilter = (
  filterName: string,
  filterValue: IRange,
) => {
  if (
    isObject(filterValue) &&
    ('range' in filterValue) &&
    (('min' in filterValue.range) &&
    ('max' in filterValue.range))
  ) {
    return `${ MAP_FILTERS_NAME[filterName] }: ${ filterValue.range.min }%-${ filterValue.range.max }%`;
  }

  return undefined;
};

const yieldSizeRangeFilter = (
  filterName: string,
  filterValue: IRange,
) => {
  if (
    isObject(filterValue) &&
    ('range' in filterValue) &&
    (('min' in filterValue.range) &&
    ('max' in filterValue.range))
  ) {
    const value = `${ Math.round(filterValue.range.min * 100) }-${ Math.round(filterValue.range.max * 100) }`;

    return `${ MAP_FILTERS_NAME[filterName] }: ${ value }`;
  }

  return undefined;
};

const upsizedFilter = (filterName: string, filterValue: string) => {
  if (filterValue) {
    return MAP_FILTERS_NAME[filterName];
  }

  return undefined;
};

const tenorRangeFilter = (
  filterName: string,
  filterValue: IRange,
) => {
  if (
    isObject(filterValue) &&
    ('range' in filterValue) &&
    (('min' in filterValue.range) &&
    ('max' in filterValue.range))
  ) {
    return `${ MAP_FILTERS_NAME[filterName] }: ${ filterValue.range.min }-${ filterValue.range.max }`;
  }

  return undefined;
};

const maturityDateRangeFilter = (
  filterName: string,
  filterValue: IRange,
) => {
  if (
    isObject(filterValue) &&
    ('range' in filterValue) &&
    (('min' in filterValue.range) &&
    ('max' in filterValue.range))
  ) {
    return `${ MAP_FILTERS_NAME[filterName] }: ${ filterValue.range.min }-${ filterValue.range.max }`;
  }

  return undefined;
};

const timeRangeFilter = (
  filterName: string,
  { range }: IRange,
) => {
  if (isObject(range) && (('min' in range) && ('max' in range))) {
    const min = format(addSeconds(startOfToday(), range.min), TIME_FORMAT_WITH_SECONDS);
    const max = format(addSeconds(startOfToday(), range.max), TIME_FORMAT_WITH_SECONDS);

    return `${ MAP_FILTERS_NAME[filterName] }: ${ min }-${ max }`;
  }

  return undefined;
};

const booleanFilter = (filterName: string) => MAP_FILTERS_NAME[filterName];

export function createFiltersFormatter(config: IFiltersFormatterConfig = {}) {
  const mapCurrentFaceRangeIds = (
    currentFaceRangeIds: string[],
    { minValue, maxValue }: IMaxMinValues,
  ) => (
    (config.currentFaceRangePreset || [])
      .filter((presetItem) => currentFaceRangeIds.indexOf(presetItem.currentFaceRangeId) !== -1)
      .map((item) => {
        const fromValue = formatNumber(item.currentFaceRangeFromValue || minValue);
        const toValue = formatNumber(item.currentFaceRangeToValue || maxValue);
        return `${ fromValue }-${ toValue }`;
      })
  );

  const currentFaceRangeIdsFilter = (
    filterName: string,
    filterValue: string[],
    filterData: IMaxMinValues,
  ) => {
    const value = Array.isArray(filterValue) && mapCurrentFaceRangeIds(filterValue, filterData).join('; ');

    return `${ MAP_FILTERS_NAME[filterName] }: ${ value }`;
  };

  const filtersFormatterMap = {
    // Deal
    dates: getDateFromToRangeFilter,
    includeInvestorIds: includedRegistrationGroupIdsFilter,
    includeContactIds: includedRegistrationGroupIdsFilter,
    // Cross Deal
    includeTransactionTypeIds: includedRegistrationGroupIdsFilter,
    includeSubIndustryIds: includedRegistrationGroupIdsFilter,
    includeAbsSubSectorIds: includedRegistrationGroupIdsFilter,
    includeAccountTypeIds: includedRegistrationGroupIdsFilter,
    includeAccountIds: includedRegistrationGroupIdsFilter,
    includeCurrencyIds: includedRegistrationGroupIdsFilter,
    includeDealIds: includedRegistrationGroupIdsFilter,
    includeRatingGroupIds: includedRegistrationGroupIdsFilter,
    includeRatingAgencyIds: includedRegistrationGroupIdsFilter,
    includeDebtCouponTypeIds: includedRegistrationGroupIdsFilter,
    includeSponsorIds: includedRegistrationGroupIdsFilter,
    includeUnderwriterIds: includedRegistrationGroupIdsFilter,
    includeOwnerIds: includedRegistrationGroupIdsFilter,
    allocationRange: allocationSizeRangeFilter,
    spreadSizeRange: tenorRangeFilter,
    tenorWalYearsRange: tenorRangeFilter,
    yieldSizeRange: yieldSizeRangeFilter,
    upsized: upsizedFilter,
    allocationTickers: includedRegistrationGroupIdsFilter,
    allocationClasses: includedRegistrationGroupIdsFilter,
    enhancedRegionCountryCollection: includedRegistrationGroupIdsFilter,
    investorRegionCountryCollection: includedRegistrationGroupIdsFilter,
    // NRSRO
    includedDocumentTypeIds: includedRegistrationGroupIdsFilter,
    includedPseudoSectorIds: includedRegistrationGroupIdsFilter,
    includedPublisherIds: includedRegistrationGroupIdsFilter,
    // Bonds and Bondscreener
    includedRegionIds: includedRegistrationGroupIdsFilter,
    includedCurrencyIds: includedRegistrationGroupIdsFilter,
    includedSubsectorIds: includedRegistrationGroupIdsFilter,
    includedIssuerIds: includedRegistrationGroupIdsFilter,
    includedDealerIds: includedRegistrationGroupIdsFilter,
    includedRatingGroupIds: includedRegistrationGroupIdsFilter,
    includedRatingAgencyIds: includedRegistrationGroupIdsFilter,
    includedBenchmarkGroupIds: includedRegistrationGroupIdsFilter,
    includedSpeedTypeIds: includedRegistrationGroupIdsFilter,
    includedStatus: includedStatusFilter,
    currentFaceRange: spreadRangeFilter,
    currentFaceRangeIds: currentFaceRangeIdsFilter,
    spreadRange: spreadRangeFilter,
    walRange: speedRangeFilter,
    speedRange: speedRangeFilter,
    // Bondscreener
    includedSubindustryIds: includedRegistrationGroupIdsFilter,
    includedCouponTypeIds: includedRegistrationGroupIdsFilter,
    includedRegistrationGroupIds: includedRegistrationGroupIdsFilter,
    includedProductIds: includedRegistrationGroupIdsFilter,
    trancheSizeRange: speedRangeFilter,
    dealSizeRange: speedRangeFilter,
    pricingSpreadRange: speedRangeFilter,
    weightedAverageLifeRange: speedRangeFilter,
    couponRange: yieldRangeFilter,
    tenorRange: tenorRangeFilter,
    maturityDateRange: maturityDateRangeFilter,
    includedRankingIds: includedRegistrationGroupIdsFilter,
    yieldRange: yieldRangeFilter,
    isUpsized: booleanFilter,
    includedParentIds: includedRegistrationGroupIdsFilter,
    // Trace
    maturityDate: speedRangeFilter,
    price: speedRangeFilter,
    coupon: speedRangeFilter,
    size: speedRangeFilter,
    tradeDate: dateRangeFilter,
    tradeTime: timeRangeFilter,
    includedContraPartyTypes: includedRegistrationGroupIdsFilter,
    includedRemunerations: includedRegistrationGroupIdsFilter,
    includedReportingPartyTypes: includedRegistrationGroupIdsFilter,
    includedReportingPartySides: (name, value) => includedTradeIdsFilter(name, value, config.tradeSideLabelMap || {}),
    includedTradeStatuses: (name, value) => includedTradeIdsFilter(name, value, config.tradeStatusLabelMap || {}),
    includedStatusIds: includedRegistrationGroupIdsFilter,
    // Articles
    includedDealIds: includedRegistrationGroupIdsFilter,
    includedSectorIds: includedRegistrationGroupIdsFilter,
  };

  return function filtersFormatter(
    filterName: string,
    filterValue: string | string[] | IRange | IPeriod | IMaxMinValues,
    filterData: any,
  ) {
    return filtersFormatterMap[filterName] ?
      filtersFormatterMap[filterName](filterName, filterValue, filterData) : filterValue;
  };
}
